import type { CatalogIndex } from '../catalog/types'
import { LEVEL_PREREQ_TYPES, QUALITIES, TAGS, type ItemRef, type Quest } from './types'

export type Severity = 'error' | 'warning'

export interface Issue {
  severity: Severity
  /** Dotted path into the quest, e.g. "requirements[1].item". */
  field: string
  message: string
}

const QUALITY_SET = new Set<string>(QUALITIES)
const TAG_SET = new Set<string>(TAGS)
const LEVEL_TYPES = new Set<string>(LEVEL_PREREQ_TYPES)

/**
 * Mirrors the Unity importer's checks. One bad reference aborts the whole import
 * batch, so catching these before the commit is cheaper than a failed import.
 *
 * Errors block saving; warnings don't.
 */
export function validateQuest(
  quest: Quest,
  catalog: CatalogIndex,
  knownQuestIds: ReadonlySet<string>,
): Issue[] {
  const issues: Issue[] = []
  const error = (field: string, message: string) =>
    issues.push({ severity: 'error', field, message })
  const warn = (field: string, message: string) =>
    issues.push({ severity: 'warning', field, message })

  if (!quest.id.trim()) error('id', 'Quest id is required — it is also the filename.')
  else if (!/^[A-Za-z0-9._-]+$/.test(quest.id))
    error('id', `"${quest.id}" is not a safe filename. Use letters, digits, dot, dash, underscore.`)

  if (!quest.label.trim()) warn('label', 'No label — the player sees this as the quest title.')
  if (!TAG_SET.has(quest.tag)) error('tag', `"${quest.tag}" is not a valid tag.`)

  // Item references must resolve, case-sensitively, against the catalogs.
  const checkRefs = (refs: ItemRef[], field: string) => {
    refs.forEach((ref, index) => {
      const path = `${field}[${index}]`
      const item = catalog.items.get(ref.item)
      if (!ref.item) error(`${path}.item`, 'No item selected.')
      else if (!item)
        error(`${path}.item`, `"${ref.item}" is not in catalog/items.json. Import would abort.`)
      if (!QUALITY_SET.has(ref.quality))
        error(`${path}.quality`, `"${ref.quality}" is not a valid quality.`)
      if (!Number.isInteger(ref.amount) || ref.amount < 1)
        error(`${path}.amount`, 'Amount must be a whole number of at least 1.')
      if (item?.seasonal && (quest.tag === 'MAIN' || quest.tag === 'TUTORIAL'))
        warn(
          `${path}.item`,
          `${item.label} is seasonal — a ${quest.tag} quest requiring it can block players until the event returns.`,
        )
    })
  }
  checkRefs(quest.requirements, 'requirements')
  checkRefs(quest.requirementsToShow, 'requirementsToShow')
  checkRefs(quest.rewards, 'rewards')

  quest.prereqs.forEach((prereq, index) => {
    const path = `prereqs[${index}]`
    if (prereq.type === 'QuestCompleted') {
      if (!prereq.quest) error(`${path}.quest`, 'No quest selected.')
      else if (!knownQuestIds.has(prereq.quest))
        error(`${path}.quest`, `"${prereq.quest}" is not a known quest id.`)
      else if (prereq.quest === quest.id)
        error(`${path}.quest`, 'A quest cannot require itself — it would never appear.')
    } else if (prereq.type === 'LocationVisit') {
      if (!catalog.locations.has(prereq.location))
        error(`${path}.location`, `"${prereq.location}" is not in catalog/locations.json.`)
      if (!Number.isInteger(prereq.value) || prereq.value < 1)
        error(`${path}.value`, 'Visit count must be a whole number of at least 1.')
    } else if (LEVEL_TYPES.has(prereq.type)) {
      if (!Number.isInteger(prereq.value) || prereq.value < 0)
        error(`${path}.value`, 'Level must be a whole number of 0 or more.')
    } else {
      error(`${path}.type`, `"${(prereq as { type: string }).type}" is not a valid prereq type.`)
    }
  })

  quest.estateRequirement.forEach((estateId, index) => {
    if (!catalog.estates.has(estateId))
      error(`estateRequirement[${index}]`, `"${estateId}" is not in catalog/estates.json.`)
  })

  quest.boostRequirement.forEach((boost, index) => {
    if (!catalog.boosts.has(boost.boost))
      error(`boostRequirement[${index}].boost`, `"${boost.boost}" is not in catalog/boosts.json.`)
    if (!Number.isInteger(boost.amount) || boost.amount < 1)
      error(`boostRequirement[${index}].amount`, 'Amount must be a whole number of at least 1.')
  })

  quest.specialRewards.forEach((reward, index) => {
    const path = `specialRewards[${index}]`
    switch (reward.type) {
      case 'Tool':
        if (!catalog.tools.has(reward.tool))
          error(`${path}.tool`, `"${reward.tool}" is not in catalog/tools.json.`)
        break
      case 'Recipe':
        // A Recipe reward pointing at a resource rather than a craftable is rejected on import.
        if (!catalog.items.has(reward.recipe))
          error(`${path}.recipe`, `"${reward.recipe}" is not in catalog/items.json.`)
        else if (!catalog.craftables.has(reward.recipe))
          error(
            `${path}.recipe`,
            `"${reward.recipe}" is a resource. Recipe rewards must point at a craftable.`,
          )
        break
      case 'Skin':
        if (!catalog.skins.has(reward.skin))
          error(`${path}.skin`, `"${reward.skin}" is not in catalog/skins.json.`)
        break
      case 'Boost':
        if (!catalog.boosts.has(reward.boost))
          error(`${path}.boost`, `"${reward.boost}" is not in catalog/boosts.json.`)
        if (!Number.isInteger(reward.amount) || reward.amount < 1)
          error(`${path}.amount`, 'Amount must be a whole number of at least 1.')
        break
      default:
        error(`${path}.type`, 'Unknown special reward type.')
    }
  })

  for (const field of [
    'goldRequirement',
    'goldRequirementToShow',
    'renownRequirement',
    'goldReward',
    'challengeStarsObtained',
  ] as const) {
    const value = quest[field]
    if (!Number.isInteger(value) || value < 0)
      error(field, 'Must be a whole number of 0 or more.')
  }

  // challengeStarsObtained is checked instead of item requirements, not alongside them.
  if (quest.challengeStarsObtained > 0 && quest.requirements.length > 0)
    warn(
      'challengeStarsObtained',
      'Challenge stars are checked instead of item requirements. Combining them means the items are never asked for.',
    )

  return issues
}

export function hasErrors(issues: Issue[]): boolean {
  return issues.some((issue) => issue.severity === 'error')
}
